/**
 * Auto-fix framework (spec §5.1): `(doc, issues, options) => { doc, fixed,
 * unfixable }`. Pure and deterministic — it never mutates the input document.
 * Each fixable rule supplies a fixer that re-resolves all its violations; the
 * framework runs the enabled fixers in catalog order (deterministic) and
 * classifies every input issue as `fixed` or `unfixable`.
 *
 * Fixes are individually toggleable via {@link AutoFixOptions.enabled}: omit it
 * to fix every fixable rule, or pass an allowlist of rule ids (e.g. the QC panel
 * fixing one rule at a time).
 *
 * Two engine-level guarantees (the fixers themselves cannot provide them):
 *
 * 1. **Zero new issues.** A fixer only knows its own rule's config, so its
 *    conservative floors cannot encode cross-rule constraints (e.g. a min-gap
 *    shift shortening a cue past the preset's CPS limit). After each rule's fix
 *    the document is re-evaluated against the full ruleset; if the fix raised
 *    any rule's issue count, the fix is reverted and its issues classified as
 *    unfixable — exactly like a fixer that declines. Counts (not
 *    (ruleId, cueIndex) pairs) are compared so that a fix which legitimately
 *    removes a cue and renumbers the survivors (empty-cue) is not mistaken for
 *    one that introduced a new issue (cue-forge-sgh).
 * 2. **Invisible cues are untouched.** `evaluate` hides non-Dialogue (ASS
 *    Comment) events from the rules, so fixers must not see them either: the
 *    empty-cue fix would delete "empty" comments and the timing fixes would
 *    shift dialogue around invisible 0-length events. Comments are extracted
 *    before the fixers run and re-merged at their original positions after.
 *
 * The same extract-and-re-merge shields {@link AutoFixOptions.protect} cues — the
 * ones an operator has accepted as intentional (cue-forge-n3e.7). A fixer
 * re-scans the whole document, so without this a Fix-all that resolves one cue's
 * over-CPS issue would also rewrite an accepted over-CPS credit the operator
 * explicitly chose to keep. Protected cues are hidden from the fixers (and from
 * the zero-new-issues evaluation) and re-merged unchanged afterwards.
 */

import type { Cue, SubtitleDocument } from '../model/types';
import { evaluate, isVisibleCue } from './evaluate';
import { RULES } from './rules';
import type { RuleRegistry } from './rule';
import { RULE_IDS, type Ruleset } from './ruleset.schema';
import type { QCIssue, RuleId } from './types';

export interface AutoFixOptions {
  /** Supplies each fixer its parameters; pair the ruleset that produced the issues. */
  ruleset: Ruleset;
  /** Rule ids to fix; omit to fix every fixable rule. Lets the UI toggle fixes. */
  enabled?: RuleId[];
  /**
   * Cues to shield from the (whole-document) fixers — the operator's accepted
   * "intentional" cues (cue-forge-n3e.7). Matched by object identity, so pass
   * cue objects from the same document. Hidden before fixers run, re-merged
   * unchanged after, like invisible events.
   */
  protect?: ReadonlySet<Cue>;
}

export interface AutoFixResult {
  doc: SubtitleDocument;
  fixed: QCIssue[];
  unfixable: QCIssue[];
}

/**
 * Per-rule issue counts for the zero-new-issues invariant. Keying on a
 * `(ruleId, cueIndex)` pair breaks when a fix removes a cue (e.g. empty-cue):
 * the survivors are renumbered, so a pre-existing issue on a later cue
 * reappears at a lower index and looks "new". Comparing the *count* of issues
 * per rule is robust to that renumbering — and to a fixer that changes a cue's
 * text while a same-cue issue persists — while still catching a genuinely new
 * problem (a rule whose total goes up, including a brand-new rule going 0→n).
 */
function ruleCounts(issues: QCIssue[]): Map<RuleId, number> {
  const counts = new Map<RuleId, number>();
  for (const i of issues) counts.set(i.ruleId, (counts.get(i.ruleId) ?? 0) + 1);
  return counts;
}

/** True if any rule has more issues after the fix than before it. */
function introducesNewIssue(before: Map<RuleId, number>, after: Map<RuleId, number>): boolean {
  for (const [rule, n] of after) {
    if (n > (before.get(rule) ?? 0)) return true;
  }
  return false;
}

export function autoFix(
  doc: SubtitleDocument,
  issues: QCIssue[],
  options: AutoFixOptions,
  registry: Partial<RuleRegistry> = RULES,
): AutoFixResult {
  const allow = options.enabled ? new Set<RuleId>(options.enabled) : null;
  const fixed: QCIssue[] = [];
  const unfixable: QCIssue[] = [];

  // Set aside cues the fixers must not see — non-Dialogue events (guarantee 2)
  // and operator-protected "accepted" cues (cue-forge-n3e.7) — remembering each
  // one's original array position for the re-merge below.
  const protect = options.protect;
  const hidden: { cue: Cue; at: number }[] = [];
  const visible: Cue[] = [];
  doc.cues.forEach((cue, at) => {
    if (isVisibleCue(cue) && !protect?.has(cue)) visible.push(cue);
    else hidden.push({ cue, at });
  });

  let current: SubtitleDocument = hidden.length > 0 ? { ...doc, cues: visible } : doc;
  // Issue counts already flagged before the next fix runs — the baseline for
  // the zero-new-issues check. One full evaluation per *fixing* rule, well
  // inside the <1s full-QC budget (fixers are per-rule, not per-cue).
  let known = ruleCounts(evaluate(current, options.ruleset, registry));

  for (const id of RULE_IDS) {
    const ruleIssues = issues.filter((i) => i.ruleId === id);
    if (ruleIssues.length === 0) continue;

    const def = registry[id];
    const config = options.ruleset.rules[id];
    const toggledOn = allow === null || allow.has(id);
    const canFix = def?.fix !== undefined && config !== undefined && def.fixable && toggledOn;

    if (!canFix) {
      unfixable.push(...ruleIssues);
      continue;
    }

    // Config type matches the rule id by construction; the union is sound here.
    // A rule with a cue-local `fixCue` (cue-forge-zxw.12) is scoped to just the
    // cues named by the issues being fixed, so fix-one lands the clicked cue even
    // when the whole-document fix would be reverted by an unrelated cue. The
    // zero-new-issues guard below still runs doc-wide, so the scoping is safe.
    let candidate: SubtitleDocument;
    if (def.fixCue !== undefined) {
      const targets = new Set(ruleIssues.map((i) => i.cueIndex));
      const fixOneCue = (cue: Cue): Cue =>
        (def.fixCue as (c: Cue, cfg: typeof config) => Cue)(cue, config);
      candidate = {
        ...current,
        cues: current.cues.map((cue) => (targets.has(cue.index) ? fixOneCue(cue) : cue)),
      };
    } else {
      candidate = (def.fix as (d: SubtitleDocument, c: typeof config) => SubtitleDocument)(
        current,
        config,
      );
    }
    const afterCounts = ruleCounts(evaluate(candidate, options.ruleset, registry));
    if (introducesNewIssue(known, afterCounts)) {
      // The fix would introduce a new issue (guarantee 1): revert it and leave
      // the rule's issues flagged, same as a fixer that declines.
      unfixable.push(...ruleIssues);
      continue;
    }
    current = candidate;
    known = afterCounts;
    fixed.push(...ruleIssues);
  }

  // Re-merge the hidden cues at their original positions (ascending order keeps
  // each original offset valid; clamped when fixes removed visible cues).
  if (hidden.length > 0) {
    const cues = [...current.cues];
    for (const { cue, at } of hidden) cues.splice(Math.min(at, cues.length), 0, cue);
    current = { ...current, cues };
  }

  return { doc: current, fixed, unfixable };
}
